import {
  assign,
  createMachine,
  InterpreterFrom,
  Prop,
  StateFrom,
} from 'xstate';

import {
  Context,
  EquipmentsMap,
  EquipmentType,
  Events,
} from './equipmentsPicker.schema';

/** This should never be mutated it is also used to reset! */
const initialContext: Context = {
  data: new Map(),
  lastAction: 'OTHER',
  modalName: 'MASS_EDIT',
  deselectedItems: [],
  filter: {
    queryFilter: {},
    editFilter: {},
    queryFilterUpdatedAt: 0,
  },
};

export const equipmentsPicker = createMachine(
  {
    id: 'equipments-picker',
    initial: 'idle',
    states: {
      picking: {
        type: 'parallel',
        states: {
          'select-all': {
            initial: 'idle',
            states: {
              idle: {
                on: {
                  'SELECT-ALL': {
                    target: 'loading',
                    actions: 'updateQueryFilter',
                  },
                },
              },
              loading: {
                on: {
                  'STOP-SELECT-ALL': {
                    target: 'idle',
                  },
                  DONE: {
                    target: 'idle',
                    actions: [
                      'setEquipments',
                      'setLastAction',
                      'clearDeselected',
                      'updateEditFilter',
                    ],
                  },
                },
              },
            },
          },
          pick: {
            on: {
              'REMOVE-ALL': {
                actions: ['resetMaps', 'setLastAction', 'clearDeselected'],
              },
              REMOVE: {
                actions: ['removeEquipment', 'addDeselected', 'setLastAction'],
              },
              ADD: {
                actions: ['addEquipment', 'setLastAction', 'removeDeselected'],
              },
            },
          },
        },
        on: {
          OPEN: {
            target: 'launched',
          },
          CANCEL: {
            target: 'idle',
          },
        },
      },
      idle: {
        entry: 'resetToInitial',
        on: {
          'MASS-EDIT': {
            target: 'picking',
          },
          LAUNCH: {
            target: 'launched',
            actions: ['setEquipments', 'setLastAction'],
          },
        },
      },
      launched: {
        entry: 'toggleModal',
        on: {
          RESUME: {
            target: 'picking',
            actions: 'setEquipments',
          },
          COMPLETE: {
            target: 'idle',
          },
        },
      },
    },
    tsTypes: {} as import('./equipmentsPicker.typegen').Typegen0,
    schema: {
      context: {} as Context,
      events: {} as Events,
    },
    context: initialContext,
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      addEquipment: assign({
        data: (context, event) => {
          const { data } = event;
          const { data: contextData } = context;
          const preexists = contextData.has(data.id);
          return doAddEquipment(contextData, data, preexists);
        },
      }),

      setEquipments: assign({
        // Expect this to have everything
        data: (_, event) => event.data,
      }),
      updateQueryFilter: assign({
        filter: (context, event) => {
          const { filter, time } = event;
          return {
            editFilter: context.filter.editFilter,
            queryFilter: filter,
            queryFilterUpdatedAt: time,
          };
        },
      }),
      updateEditFilter: assign({
        filter: context => {
          return {
            editFilter: context.filter.queryFilter,
            queryFilter: context.filter.queryFilter,
            queryFilterUpdatedAt: context.filter.queryFilterUpdatedAt,
          };
        },
      }),
      removeEquipment: assign({
        data: (context, event) => {
          const { data } = context;
          const { equipmentId } = event;
          return deleteEquipment(data, equipmentId);
        },
      }),
      resetToInitial: assign(initialContext),
      resetMaps: assign({
        data: _ => new Map(),
      }),

      setLastAction: assign({
        lastAction: (ctx, event) => {
          const { lastAction } = ctx;
          if (event.type === 'DONE') return 'SELECT-ALL';
          if (event.type === 'LAUNCH') return 'LAUNCH';
          // it's still select-all if we deselect some
          if (lastAction === 'SELECT-ALL' && event.type === 'REMOVE') {
            return 'SELECT-ALL';
          }
          // FIXME A user can deselect some items and add items from a different filter. Support "Added items" in lambda
          // if (lastAction === 'SELECT-ALL' && event.type === 'ADD') {
          //   return 'SELECT-ALL';
          // }
          return 'OTHER';
        },
      }),
      addDeselected: assign({
        deselectedItems: (ctx, event) => {
          const { deselectedItems } = ctx;
          const { equipmentId } = event;
          const newDeselected = [...deselectedItems, equipmentId];
          return newDeselected;
        },
      }),
      removeDeselected: assign({
        deselectedItems: (ctx, event) => {
          const { id } = event.data;
          const { deselectedItems } = ctx;
          const newDeselected = deselectedItems.filter(value => value !== id);
          return newDeselected;
        },
      }),
      clearDeselected: assign({
        deselectedItems: _ => [],
      }),
    },
  },
);

export type EQUIPMENTS_PICKER_STATE_TYPE = StateFrom<typeof equipmentsPicker>;
export type EQUIPMENTS_PICKER_SEND_TYPE = Prop<
  InterpreterFrom<typeof equipmentsPicker>,
  'send'
>;
// ExtractEvent<Events, 'DONE'>,
function doAddEquipment(
  map: EquipmentsMap,
  data: EquipmentType,
  lock: boolean,
): EquipmentsMap {
  if (lock) return map;
  const mapClone = new Map(map.entries());
  mapClone.set(data.id, data);
  return mapClone;
}
function deleteEquipment(map: EquipmentsMap, id: string) {
  const clone = new Map(map.entries());
  clone.delete(id);
  return clone;
}
